import { apiClient } from '@/lib/apiClient';

/**
 * Report endpoints are mounted twice on the backend — once under
 * `/super_admin/reports` and once under `/irancharsouq_admin/reports` — with
 * identical shapes but different data scopes. The caller's active role picks
 * the prefix; the backend enforces that the prefix matches the role.
 */
export const REPORT_PREFIX_BY_ROLE = {
    super_admin: '/api/v1/super_admin/reports',
    irancharsouq_admin: '/api/v1/irancharsouq_admin/reports',
};

export const reportPrefix = (activeRole) =>
    REPORT_PREFIX_BY_ROLE[activeRole] ?? REPORT_PREFIX_BY_ROLE.super_admin;

/**
 * Reports that only make sense for the whole platform. The backend answers 403
 * for an admin-scoped caller, so the UI hides them rather than showing an error.
 */
export const PLATFORM_ONLY_REPORTS = new Set([
    'finance',
    'customers',
    'topCustomers',
    'vendors',
]);

/**
 * Drop empty values so we never send `?status=` (which the backend would read
 * as an explicit "no status" filter rather than "no filter at all").
 */
const clean = (query) =>
    Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );

const call = async (prefix, path, query) => {
    const { data, error } = await apiClient.GET(`${prefix}${path}`, {
        params: { query: clean(query) },
    });
    if (error) throw error;
    return data;
};

/**
 * Build the 13 report fetchers bound to a role-specific prefix.
 */
export const createReportsApi = (activeRole) => {
    const prefix = reportPrefix(activeRole);
    return {
        salesSummary: (query) => call(prefix, '/sales/summary', query),
        salesTimeseries: (query) => call(prefix, '/sales/timeseries', query),
        salesByWeekday: (query) => call(prefix, '/sales/by-weekday', query),
        salesByHour: (query) => call(prefix, '/sales/by-hour', query),
        salesByMonth: (query) => call(prefix, '/sales/by-month', query),
        topProducts: (query) => call(prefix, '/products/top', query),
        inventory: (query) => call(prefix, '/products/inventory', query),
        categoryPerformance: (query) => call(prefix, '/categories/performance', query),
        orderFunnel: (query) => call(prefix, '/orders/funnel', query),
        financeOverview: (query) => call(prefix, '/finance/overview', query),
        customerCohorts: (query) => call(prefix, '/customers/cohorts', query),
        topCustomers: (query) => call(prefix, '/customers/top', query),
        vendorPerformance: (query) => call(prefix, '/vendors/performance', query),
    };
};
